import { useCallback, useEffect, useRef, useState } from 'react';
import { SuperBarViewState } from '../context/use-chat-context';

const COLLAPSE_ANIMATION_DELAY_MS = 600;
const EXPAND_CHARACTER_THRESHOLD = 45;

interface UseTextOverflowOptions {
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  value: string;
  isFocused: boolean;
  superBarViewState: SuperBarViewState;
  setSuperBarViewState: (state: SuperBarViewState) => void;
}

interface UseTextOverflowReturn {
  showTruncatedOverlay: boolean;
  expandIfOverflowing: () => void;
}

export const useTextOverflow = ({
  inputRef,
  value,
  isFocused,
  superBarViewState,
  setSuperBarViewState,
}: UseTextOverflowOptions): UseTextOverflowReturn => {
  const [textOverflows, setTextOverflows] = useState(false);
  const prevViewStateRef = useRef(superBarViewState);

  // Stable identity stops useVoiceTranscript's effect from re-applying
  // stale transcripts over post-dictation edits.
  const valueRef = useRef(value);
  valueRef.current = value;
  const viewStateRef = useRef(superBarViewState);
  viewStateRef.current = superBarViewState;

  const expandIfOverflowing = useCallback(() => {
    if (viewStateRef.current !== SuperBarViewState.DEFAULT) return;

    if (valueRef.current.length > EXPAND_CHARACTER_THRESHOLD) {
      setSuperBarViewState(SuperBarViewState.EXPANDED);
    }
  }, [setSuperBarViewState]);

  // Detect text overflow for truncation display when blurred in default mode
  useEffect(() => {
    const wasExpanded = prevViewStateRef.current === SuperBarViewState.EXPANDED;
    prevViewStateRef.current = superBarViewState;

    if (
      !isFocused &&
      superBarViewState === SuperBarViewState.DEFAULT &&
      value
    ) {
      if (wasExpanded) {
        // Collapsing from expanded - show truncation immediately if length exceeds threshold
        setTextOverflows(value.length > EXPAND_CHARACTER_THRESHOLD);
        const timer = setTimeout(() => {
          setTextOverflows(value.length > EXPAND_CHARACTER_THRESHOLD);
        }, COLLAPSE_ANIMATION_DELAY_MS);
        return () => clearTimeout(timer);
      }

      setTextOverflows(value.length > EXPAND_CHARACTER_THRESHOLD);
      return;
    }
    setTextOverflows(false);
  }, [isFocused, superBarViewState, value]);

  const showTruncatedOverlay =
    textOverflows &&
    !isFocused &&
    superBarViewState !== SuperBarViewState.EXPANDED;

  return { showTruncatedOverlay, expandIfOverflowing };
};
